// GeoJSON utility functions for processing, converting, and simplifying GeoJSON data
// Extracted from NewAssetModal.tsx to be reusable across the application

import type { Feature, Polygon } from 'geojson';
import * as toGeoJSON from '@tmcw/togeojson';
import * as turf from '@turf/turf';
import { downloadTextFile, toFileSlug } from './fileUtils';

/**
 * Calculates the size of a GeoJSON object in bytes
 */
export const getGeoJSONSize = (geojson: any): number => {
  const stringified = JSON.stringify(geojson);
  return new Blob([stringified]).size;
};

/**
 * Reduces coordinate precision to specified decimal places
 * This helps reduce file size while maintaining sufficient accuracy
 * @param geojson - GeoJSON FeatureCollection or Feature
 * @param precision - Number of decimal places (default: 5, ~1.1m accuracy at equator)
 * @returns GeoJSON with reduced coordinate precision
 */
export const reduceCoordinatePrecision = (geojson: any, precision: number = 5): any => {
  // Create a deep copy to avoid mutating the original
  const copy = JSON.parse(JSON.stringify(geojson));

  // Function to process coordinates recursively, reducing precision
  const processCoordinates = (coords: any): any => {
    if (!Array.isArray(coords)) return coords;

    // If this is a coordinate pair [lng, lat] or [lng, lat, elevation]
    if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return coords.map(coord =>
        typeof coord === 'number' ? parseFloat(coord.toFixed(precision)) : coord
      );
    }

    // Otherwise it's an array of arrays (nested structure)
    return coords.map(c => processCoordinates(c));
  };

  // Process each feature in the collection
  if (copy.features && copy.features.length > 0) {
    copy.features = copy.features.map((feature: any) => {
      if (feature.geometry && feature.geometry.coordinates) {
        feature.geometry.coordinates = processCoordinates(feature.geometry.coordinates);
      }
      return feature;
    });
  }

  return copy;
};

/**
 * Simplifies GeoJSON using Douglas-Peucker algorithm via turf.js
 * @param geojson - GeoJSON FeatureCollection
 * @param tolerance - Simplification tolerance (higher = more simplified)
 * @returns Simplified GeoJSON
 */
export const simplifyGeoJSON = (geojson: any, tolerance: number): any => {
  // Create a deep copy to avoid mutating the original
  const copy = JSON.parse(JSON.stringify(geojson));

  // Process each feature in the collection
  if (copy.features && copy.features.length > 0) {
    copy.features = copy.features.map((feature: any) => {
      // Skip point geometries as they can't be simplified
      if (feature.geometry.type === "Point" || feature.geometry.type === "MultiPoint") {
        return feature;
      }

      // Use turf.simplify with Douglas-Peucker algorithm
      try {
        const simplified = turf.simplify(feature, {
          tolerance: tolerance,
          highQuality: true // Use Douglas-Peucker algorithm
        });
        return simplified;
      } catch (error) {
        console.error("Error simplifying feature:", error);
        return feature; // Return original on error
      }
    });
  }

  return copy;
};

/**
 * Main processing function for uploaded GeoJSON data
 * - Ensures FeatureCollection format
 * - Reduces coordinate precision to 5 decimal places (~1.1m accuracy)
 * - Checks file size (0.4MB limit)
 * - Simplifies geometry if needed to meet size constraints
 * @param geoJsonData - Raw GeoJSON data (Feature or FeatureCollection)
 * @param maxSizeBytes - Maximum allowed size in bytes (default: 0.4MB)
 * @returns Processed GeoJSON ready for storage
 */
export const processGeoJsonData = (
  geoJsonData: any,
  maxSizeBytes: number = 0.4 * 1024 * 1024
): any => {
  // Handle case where we might not have a FeatureCollection
  let processedGeoJson = geoJsonData;

  if (geoJsonData.type !== 'FeatureCollection') {
    // Convert single feature to FeatureCollection
    processedGeoJson = {
      type: 'FeatureCollection',
      features: [geoJsonData]
    };
  }

  // If no features, throw error
  if (!processedGeoJson.features || processedGeoJson.features.length === 0) {
    throw new Error('No valid features found in the GeoJSON data.');
  }

  // First, reduce coordinate precision to 5 decimal places
  // This is approximately 1.1 meters of precision at the equator,
  // which is more than enough for most applications
  const PRECISION = 5;
  processedGeoJson = reduceCoordinatePrecision(processedGeoJson, PRECISION);

  // Check file size
  const originalSize = getGeoJSONSize(processedGeoJson);

  // Log original GeoJSON size
  console.log(`GeoJSON size after precision reduction: ${(originalSize/1024).toFixed(2)}KB (${(originalSize/1024/1024).toFixed(4)}MB)`);

  // If GeoJSON still exceeds size limit after precision reduction, apply simplification
  if (originalSize > maxSizeBytes) {
    // Start with a small tolerance and gradually increase until size is acceptable
    let tolerance = 0.0001; // Starting tolerance
    let simplifiedGeoJson = processedGeoJson;
    let currentSize = originalSize;
    let attempts = 0;
    const MAX_ATTEMPTS = 10; // Prevent infinite loops

    while (currentSize > maxSizeBytes && attempts < MAX_ATTEMPTS) {
      simplifiedGeoJson = simplifyGeoJSON(processedGeoJson, tolerance);
      currentSize = getGeoJSONSize(simplifiedGeoJson);

      // Increase tolerance for next iteration if needed
      tolerance *= 2;
      attempts++;
    }

    // Use the simplified version if we were able to reduce the size
    if (currentSize < originalSize) {
      console.log(`Simplified GeoJSON size: ${(currentSize/1024).toFixed(2)}KB (${(currentSize/1024/1024).toFixed(4)}MB)`);
      console.log(`GeoJSON simplified: ${(originalSize/1024).toFixed(2)}KB → ${(currentSize/1024).toFixed(2)}KB (tolerance: ${tolerance/2})`);

      processedGeoJson = simplifiedGeoJson;

      if (currentSize > maxSizeBytes) {
        console.warn(`Warning: The geometry is still ${(currentSize/1024/1024).toFixed(2)}MB after simplification (exceeds ${(maxSizeBytes/1024/1024).toFixed(2)}MB limit).`);
      }
    } else {
      console.warn(`Warning: The geometry (${(originalSize/1024/1024).toFixed(2)}MB) exceeds the ${(maxSizeBytes/1024/1024).toFixed(2)}MB limit and could not be simplified.`);
    }
  }

  return processedGeoJson;
};

/**
 * Converts KML string to GeoJSON
 * @param kmlContent - KML file content as string
 * @returns Processed GeoJSON
 */
export const convertKMLToGeoJSON = (kmlContent: string): any => {
  try {
    // Parse the KML file
    const parser = new DOMParser();
    const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');

    // Convert to GeoJSON
    const geoJsonData = toGeoJSON.kml(kmlDoc);

    // Process and return the GeoJSON data
    return processGeoJsonData(geoJsonData);
  } catch (error) {
    console.error('Error parsing KML file:', error);
    throw new Error('Error parsing KML file. Please make sure it is a valid KML file.');
  }
};

/**
 * Converts KMZ file to GeoJSON
 * KMZ files are zipped KML files, so we need to extract the KML first
 * @param kmzFile - File object
 * @returns Promise<GeoJSON>
 */
export const convertKMZToGeoJSON = async (kmzFile: File): Promise<any> => {
  try {
    // Dynamically import JSZip
    const JSZip = (await import('jszip')).default;

    // Read the file as array buffer
    const arrayBuffer = await kmzFile.arrayBuffer();

    // Load the zip file
    const zip = await JSZip.loadAsync(arrayBuffer);

    // Find the KML file inside the zip
    let kmlFile = null;

    // Look for doc.kml first (standard name in KMZ files)
    if (zip.files['doc.kml']) {
      kmlFile = zip.files['doc.kml'];
    } else {
      // If doc.kml doesn't exist, look for any .kml file
      const kmlFiles = Object.keys(zip.files).filter(name => name.toLowerCase().endsWith('.kml'));

      if (kmlFiles.length > 0) {
        kmlFile = zip.files[kmlFiles[0]];
      } else {
        throw new Error('No KML file found in the KMZ archive');
      }
    }

    // Extract and parse the KML content
    const kmlContent = await kmlFile.async('string');

    // Use the KML converter
    return convertKMLToGeoJSON(kmlContent);
  } catch (error) {
    console.error('Error processing KMZ file:', error);
    throw new Error('Error processing KMZ file. Please make sure it is a valid KMZ file.');
  }
};

/**
 * Generic file to GeoJSON converter
 * Handles KML, KMZ, and GeoJSON files
 * @param file - File object from input
 * @returns Promise<GeoJSON>
 */
export const fileToGeoJSON = async (file: File): Promise<any> => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  switch (fileExtension) {
    case 'kml': {
      // Read KML file
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const kmlContent = e.target?.result as string;
            const geojson = convertKMLToGeoJSON(kmlContent);
            resolve(geojson);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Failed to read KML file'));
        reader.readAsText(file);
      });
    }

    case 'kmz': {
      // Convert KMZ to GeoJSON
      return convertKMZToGeoJSON(file);
    }

    case 'geojson':
    case 'json': {
      // Read and parse GeoJSON file
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const geoJson = JSON.parse(e.target?.result as string);
            const processed = processGeoJsonData(geoJson);
            resolve(processed);
          } catch (error) {
            reject(new Error('Invalid GeoJSON file'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read GeoJSON file'));
        reader.readAsText(file);
      });
    }

    default:
      throw new Error(`Unsupported file type: ${fileExtension}. Please upload KML, KMZ, or GeoJSON files.`);
  }
};

/**
 * Converts a GeoJSON FeatureCollection to a KML string.
 * Supports Point, MultiPoint, LineString, MultiLineString, Polygon, and
 * MultiPolygon geometries. Open polygon rings are auto-closed.
 * @param geojson - GeoJSON FeatureCollection or Feature
 * @returns KML string
 */
export const geojsonToKML = (geojson: any): string => {
  const serializeCoord = (coord: number[]) => `${coord[0]},${coord[1]},0`;
  const serializeCoords = (coords: number[][]) => coords.map(serializeCoord).join(' ');

  // KML requires closed LinearRings; drawn/stored polygons may leave the ring open
  const closeRing = (ring: number[][]): number[][] => {
    if (!ring || ring.length < 3) return ring;
    const first = ring[0];
    const last = ring[ring.length - 1];
    return first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
  };

  const serializePolygon = (rings: number[][][]): string => {
    if (!rings?.length) return '';
    const [outer, ...inner] = rings;
    const outerXml = `<outerBoundaryIs><LinearRing><coordinates>${serializeCoords(closeRing(outer))}</coordinates></LinearRing></outerBoundaryIs>`;
    const innerXml = inner
      .map((ring) => `<innerBoundaryIs><LinearRing><coordinates>${serializeCoords(closeRing(ring))}</coordinates></LinearRing></innerBoundaryIs>`)
      .join('');
    return `<Polygon>${outerXml}${innerXml}</Polygon>`;
  };

  const serializeGeometry = (geometry: any): string => {
    if (!geometry) return '';
    switch (geometry.type) {
      case 'Point':
        return `<Point><coordinates>${serializeCoord(geometry.coordinates)}</coordinates></Point>`;
      case 'MultiPoint':
        return `<MultiGeometry>${(geometry.coordinates as number[][])
          .map((coord) => `<Point><coordinates>${serializeCoord(coord)}</coordinates></Point>`)
          .join('')}</MultiGeometry>`;
      case 'LineString':
        return `<LineString><coordinates>${serializeCoords(geometry.coordinates)}</coordinates></LineString>`;
      case 'MultiLineString':
        return `<MultiGeometry>${(geometry.coordinates as number[][][])
          .map((line) => `<LineString><coordinates>${serializeCoords(line)}</coordinates></LineString>`)
          .join('')}</MultiGeometry>`;
      case 'Polygon':
        return serializePolygon(geometry.coordinates);
      case 'MultiPolygon':
        return `<MultiGeometry>${(geometry.coordinates as number[][][][])
          .map(serializePolygon)
          .join('')}</MultiGeometry>`;
      case 'GeometryCollection':
        return `<MultiGeometry>${(geometry.geometries || [])
          .map(serializeGeometry)
          .join('')}</MultiGeometry>`;
      default:
        return '';
    }
  };

  const escapeXml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const features =
    geojson.type === 'FeatureCollection'
      ? geojson.features
      : geojson.type === 'Feature'
      ? [geojson]
      : [];

  const placemarks = features
    .map((feature: any, i: number) => {
      const name = escapeXml(feature.properties?.name ?? `Feature ${i + 1}`);
      const geom = serializeGeometry(feature.geometry);
      return `    <Placemark>\n      <name>${name}</name>\n      ${geom}\n    </Placemark>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Drawn Geometry</name>
${placemarks}
  </Document>
</kml>`;
};

export type GeometryDownloadFormat = 'geojson' | 'kml';

/**
 * Downloads a GeoJSON FeatureCollection as a .geojson or .kml file.
 * @param geojson - GeoJSON FeatureCollection (or Feature)
 * @param format - 'geojson' or 'kml'
 * @param baseName - Base for the filename (e.g. the asset name); sanitized for the filesystem
 */
export const downloadGeometry = (
  geojson: any,
  format: GeometryDownloadFormat,
  baseName: string
): void => {
  // Fold accented letters to their base (e.g. "Rivière" → "riviere") before stripping
  const safeName = toFileSlug(baseName, 'geometry');

  if (format === 'kml') {
    downloadTextFile(geojsonToKML(geojson), `${safeName}.kml`, 'application/vnd.google-earth.kml+xml');
  } else {
    downloadTextFile(JSON.stringify(geojson, null, 2), `${safeName}.geojson`, 'application/geo+json');
  }
};

/**
 * Gets the centroid of a GeoJSON feature
 * @param feature - GeoJSON Feature
 * @returns [lng, lat] coordinates of centroid (may include elevation as third element)
 */
export const getFeatureCentroid = (feature: any): number[] => {
  if (feature.geometry.type === "Point") {
    return feature.geometry.coordinates;
  }

  const centroid = turf.centroid(feature);
  return centroid.geometry.coordinates as number[];
};

/**
 * Gets the geometry type of the first feature in a GeoJSON FeatureCollection
 * @param geojson - GeoJSON FeatureCollection
 * @returns Geometry type string (Point, LineString, Polygon, etc.)
 */
export const getGeometryType = (geojson: any): string | null => {
  if (geojson.features && geojson.features.length > 0) {
    return geojson.features[0].geometry.type;
  }
  return null;
};

/**
 * Builds an axis-aligned rectangle Polygon feature from two opposite corners.
 * The exterior ring is wound counter-clockwise (RFC 7946) and closed.
 * @param cornerA - [lng, lat] of one corner
 * @param cornerB - [lng, lat] of the opposite corner
 * @returns GeoJSON Polygon Feature, or null if the corners share a longitude or latitude (zero area)
 */
export const buildRectangleFeature = (cornerA: number[], cornerB: number[]): Feature<Polygon> | null => {
  const west = Math.min(cornerA[0], cornerB[0]);
  const east = Math.max(cornerA[0], cornerB[0]);
  const south = Math.min(cornerA[1], cornerB[1]);
  const north = Math.max(cornerA[1], cornerB[1]);

  if (west === east || south === north) return null;

  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [[
        [west, south],
        [east, south],
        [east, north],
        [west, north],
        [west, south],
      ]],
    },
    properties: {},
  };
};
